"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldAlert, SlidersHorizontal } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function CompareValue() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="comparevaluepricingguide" data-nav="light" className="relative w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="flex flex-col items-center">
          {/* Specification Eyebrow */}
          <motion.div variants={itemVariants} className="flex items-center gap-2 mb-4 border border-border bg-muted/40 px-3 py-1 font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
            <SlidersHorizontal className="size-3.5 text-primary" />
            <span>EQUIPMENT EVALUATION</span>
          </motion.div>

          {/* Section Headline */}
          <motion.h2 variants={itemVariants} className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-center text-foreground max-w-3xl">
            Compare Value, Not Just the Headline Price
          </motion.h2>

          {/* Industrial Spec Plate Container */}
          <motion.div variants={itemVariants} className="mt-10 sm:mt-12 w-full border border-border bg-card shadow-xs">
            {/* Plate Header Bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/50 px-4 sm:px-6 py-2.5 font-mono text-[11px] sm:text-xs uppercase tracking-wider text-muted-foreground">
              <span className="font-semibold text-foreground">
                DOC REF: PRICING-ADVISORY-SPEC
              </span>
              <span>STANDARD COMMERCIAL GUIDANCE</span>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
              {/* Block 1 */}
              <div className="p-6 sm:p-8 flex flex-col justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 font-mono text-xs text-primary font-semibold tracking-wide uppercase mb-3">
                    <ShieldAlert className="size-4 shrink-0 text-primary" />
                    <span>01. Component Inclusion</span>
                  </div>
                  <p className="text-base sm:text-lg leading-relaxed text-foreground font-sans">
                    The cheapest mechanical bull is not automatically the least expensive machine to own. A lower advertised price may exclude the controller, inflatable, blower or other equipment that another package includes.
                  </p>
                </div>
                <div className="font-mono text-xs text-muted-foreground border-t border-border/60 pt-3">
                  Check: Complete package breakdown vs bare frame
                </div>
              </div>

              {/* Block 2 */}
              <div className="p-6 sm:p-8 flex flex-col justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 font-mono text-xs text-primary font-semibold tracking-wide uppercase mb-3">
                    <CheckCircle2 className="size-4 shrink-0 text-primary" />
                    <span>02. Total Lifecycle Factors</span>
                  </div>
                  <p className="text-base sm:text-lg leading-relaxed text-foreground font-sans">
                    For used equipment, condition, known repairs and parts availability can also affect value. In addition, shipping and site preparation may add costs that do not appear in the product price.
                  </p>
                </div>
                <div className="font-mono text-xs text-muted-foreground border-t border-border/60 pt-3">
                  Check: Freight, electrical feed, and ongoing serviceability
                </div>
              </div>
            </div>

            {/* Summary Anchor Statement Banner */}
            <div className="border-t border-border bg-muted/30 px-6 sm:px-8 py-5 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="font-serif text-lg sm:text-xl font-semibold uppercase tracking-wide text-foreground">
                Compare complete packages before deciding which machine represents better value.
              </p>
              <div className="hidden sm:flex items-center font-mono text-xs text-muted-foreground uppercase">
                Verification Required
              </div>
            </div>
          </motion.div>

          {/* Centered CTA Button */}
          <motion.div variants={itemVariants} className="mt-8 sm:mt-10">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-8 py-6 font-serif uppercase text-base sm:text-lg font-bold tracking-wider shadow-none transition-colors">
              <Link to="/MechanicalBullPriceGuidePage">
                <span>Read the Mechanical Bull Price Guide</span>
                <ArrowRight className="size-5 ml-1" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}